import {
  PortableText,
  type PortableTextComponents,
  type PortableTextBlock,
} from 'next-sanity';
import Image from 'next/image';
import Button from '@/componentv2/button/button';
import Link from 'next/link';
import { schemaTypeAndPageMapping } from '@/util';
import { imageBuilder, urlForImage } from '@/sanity/lib/utils';
import { Col, Row } from 'react-bootstrap';
import {
  SanityImageCrop,
  SanityImageHotspot,
  internalGroqTypeReferenceTo,
} from '@/sanity.types';
import VideoPlayer from '../videoPlayer/videoPlayer';
import { imageData } from '@/config/defaultBlur';
import Typography from '@/componentv2/typography/typography';
import type { TypographyStyle } from '@/componentv2/typography/typography';

export function LandingPortableText({
  className,
  value,
  type,
  paragraphStyle = 'paragraph',
}: {
  className?: string;
  value: PortableTextBlock[];
  type?: string;
  /** Which type-scale row `normal` blocks render as, e.g. 'paragraph-small-1' for FAQ answers. */
  paragraphStyle?: TypographyStyle;
}) {
  const components: PortableTextComponents = {
    marks: {
      link: ({ children, value }) => {
        return value?.href ? (
          <Link
            href={value.href}
            rel="noreferrer noopener"
            target={value?.target ? '_blank' : ''}
            aria-label={`Go to ${value.href}`}
          >
            {children}
          </Link>
        ) : (
          <>{children}</>
        );
      },
      internalLink: ({ children, value }) => {
        const pageName = schemaTypeAndPageMapping(value?.type, value?.pageHub);
        const slugPart = value?.slug ? `/${value.slug}` : '';
        const pathPart = pageName?.page ? `/${pageName.page}` : '';
        const internalUrl = `${pathPart}${slugPart}`;

        return internalUrl ? (
          <Link
            href={internalUrl}
            target={value?.target ? '_blank' : ''}
            aria-label={`Go to ${internalUrl}`}
          >
            {children}
          </Link>
        ) : (
          <>{children}</>
        );
      },
      textColor: ({ children, value }) => {
        return <span style={{ color: value?.value }}>{children}</span>;
      },
      highlightColor: ({ children, value }) => {
        return (
          <span style={{ backgroundColor: value?.value }}>{children}</span>
        );
      },
      alignment: ({ children, value }) => {
        const alignment = value?.align || 'left'; // default to left if missing
        let cls = '';

        if (alignment === 'center') cls = 'text-center';
        else if (alignment === 'right') cls = 'text-right';
        else cls = 'text-left';

        // A mark applies to an inline span of text within a block, so this
        // can't be a <p> — the block it marks may itself be a heading (h1-h6),
        // and a nested <p> would both be invalid HTML and pick up the global
        // `p` typographic rule instead of inheriting the heading's style.
        return <span className={cls}>{children}</span>;
      },
      gradientColor: ({ children, value }) => {
        if (!value?.gradient) return <>{children}</>;

        return (
          <span
            style={{
              backgroundImage: value.gradient,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              display: 'inline-block',
            }}
          >
            {children}
          </span>
        );
      },
    },
    // Maps each CMS-selectable block style straight to the matching row of
    // the shared type scale (see componentv2/typography/typography.tsx), so a
    // block always looks like whatever style the editor actually picked in
    // Sanity — instead of a wrapper div guessing/forcing one size onto
    // whatever tag happens to render inside it.
    block: {
      normal: ({ children }) => (
        <Typography style={paragraphStyle}>{children}</Typography>
      ),
      h1: ({ children }) => <Typography style="h1">{children}</Typography>,
      h2: ({ children }) => <Typography style="h2">{children}</Typography>,
      h3: ({ children }) => <Typography style="h3">{children}</Typography>,
      h4: ({ children }) => <Typography style="h4">{children}</Typography>,
      h5: ({ children }) => <Typography style="h5">{children}</Typography>,
      h6: ({ children }) => <Typography style="h6">{children}</Typography>,
      blockquote: ({ children }) => (
        <Typography style="quote">{children}</Typography>
      ),
    },
    types: {
      image: ({ value }) => {
        const { asset } = value;

        const imageUrl = imageBuilder.image(value).width(800).quality(75).url();
        const { width, height } = asset?.metadata?.dimensions || {
          width: 500,
          height: 350,
        };
        const blurDataURL = asset?.metadata?.lqip || imageData.blurImage;
        return (
          <div className="text-center">
            <Image
              src={(imageUrl as string) || ''}
              alt={value?.alt}
              width={width}
              height={height}
              sizes="100vw"
              placeholder="blur"
              blurDataURL={blurDataURL}
            />
            <p>{value?.caption}</p>
          </div>
        );
      },
      ctaButton: ({ value }) => {
        return type?.includes('hero') ? (
          <div className="hero-btn">
            <Button
              target={value?.target ? '_blank' : ''}
              href={value?.link || ''}
              variant="primary"
            >
              {value?.buttonText}
            </Button>
          </div>
        ) : (
          <div className="text-center">
            <Button
              target={value?.target ? '_blank' : ''}
              href={value?.link || ''}
              {...(value?.buttonColor
                ? {
                    style: {
                      backgroundColor: value?.buttonColor?.value,
                      borderColor: value?.buttonColor?.value,
                    },
                  }
                : { variant: 'primary' })}
            >
              {value?.buttonText}
            </Button>
          </div>
        );
      },
      code: ({ value }) => {
        if (!value?.code) return null;
        if (value?.language === 'html') {
          return (
            <div
              className="custom code"
              dangerouslySetInnerHTML={{
                __html: value?.code,
              }}
            />
          );
        }
        if (value?.language === 'javascript') {
          return <script>{value?.code}</script>;
        }

        if (value?.language === 'css') {
          return <style>{value?.code}</style>;
        }
        return (
          <div
            className="customCode"
            dangerouslySetInnerHTML={{ __html: value?.code }}
          />
        );
      },
      ctaGray: ({ value }) => {
        return (
          <div className="text-center">
            <div
              className={`csm-context ${value?.isGray ? 'csm-link-holder' : 'csm-no-bg'}`}
            >
              <Link
                href={value?.link || ''}
                target={value?.target ? '_blank' : ''}
                aria-label={`Go to ${value?.link}`}
                className="cta-gray-link"
              >
                {value?.text}
              </Link>
              <Link
                href={value?.link || ''}
                target={value?.target ? '_blank' : ''}
                aria-label={`Go to ${value?.link}`}
              >
                {value?.linkText}
              </Link>
            </div>
          </div>
        );
      },
      sampleSignUps: ({ value }) => {
        return (
          <div className="signupsWrap">
            <h3>{value?.title || 'Sample Sign Ups'}</h3>
            <Row className="mb-4" style={{ justifyContent: 'center' }}>
              {value?.thumbnails?.map(
                (
                  thumbnail: {
                    thumbnailLink?: string;
                    thumbnailImage: {
                      asset?: {
                        _ref: string;
                        _type: 'reference';
                        _weak?: boolean;
                        [internalGroqTypeReferenceTo]?: 'sanity.imageAsset';
                      };
                      hotspot?: SanityImageHotspot;
                      crop?: SanityImageCrop;
                      alt: string;
                      _type: 'image';
                    };
                    alt: string;
                  },
                  index: number
                ) => (
                  <Col xs={12} md={4} key={index}>
                    {thumbnail?.thumbnailLink ? (
                      <Link
                        href={thumbnail?.thumbnailLink || ''}
                        target={value?.target ? '_blank' : ''}
                        aria-label={`Go to ${thumbnail?.thumbnailLink}`}
                      >
                        <Image
                          src={
                            thumbnail?.thumbnailImage
                              ? (urlForImage(
                                  thumbnail.thumbnailImage
                                )?.url() as string)
                              : ''
                          }
                          alt={thumbnail?.alt || 'signupsWrap CTA images'}
                          width={300}
                          height={250}
                          sizes="(max-width: 767px) 100vw, 50vw"
                          placeholder="blur"
                          blurDataURL={imageData.blurImage}
                        />
                      </Link>
                    ) : (
                      <Image
                        src={
                          thumbnail?.thumbnailImage
                            ? (urlForImage(
                                thumbnail.thumbnailImage
                              )?.url() as string)
                            : ''
                        }
                        alt={thumbnail?.alt || 'signupsWrap CTA images'}
                        width={300}
                        height={250}
                        sizes="(max-width: 767px) 100vw, 50vw"
                        placeholder="blur"
                        blurDataURL={imageData.blurImage}
                      />
                    )}
                  </Col>
                )
              )}
            </Row>
            {value?.includeSUPButton ? (
              <Button
                variant="primary"
                size="lg"
                target={value?.target ? '_blank' : ''}
                href="/index.cfm?go=w.Welcome"
              >
                Create Sign Up
              </Button>
            ) : (
              <></>
            )}
          </div>
        );
      },
      contentVideo: ({ value }) => {
        return (
          <div className="video-container">
            <VideoPlayer url={value?.url} width="100%" height="auto" />
          </div>
        );
      },
    },
  };

  return (
    <div className={['prose', className].filter(Boolean).join(' ')}>
      <PortableText components={components} value={value} />
    </div>
  );
}
